import "./list-visit.scss";

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Sidebar } from "primereact/sidebar";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";

import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import SearchInput from "@shared/ui/search-input/search-input";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useQyGetVisit } from "@factory/query/visit.query";
import { GetVisitModel } from "@factory/model/query-model-visit";
import { TableSort } from "@shared/models/table-sort";
import { FilterVisit } from "../filter-visit/filter-visit";
import { useFilterVisitContext } from "../filter-visit/filter-visit.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { format } from "date-fns";
import { useQyGetContractById } from "@factory/query/contract.query";
import { Tag } from "primereact/tag";
import { getPaymentSeverity } from "@core/utility/get-severity";

export function ListVisit() {
  // THIS IS THE LOCAL DECLARATION
  const { formatDateLocal, formatTimeLocal } = useDateTimeContext();
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } = useFilterVisitContext();
  const { patientId: paramPatientId, contractId } = useParams();

  // THIS IS THE QUERY DECLARATION
  const {
    data: visitResponse,
    isLoading,
    isError,
    error,
  } = useQyGetVisit(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues()
  );

  const { data: contractResponse } = useQyGetContractById(contractId ?? "");
  const contractData = contractResponse?.data?.[0];

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetVisitModel) => {
    const name = `${item.patient_first_name} ${item.patient_last_name}`;
    navigate(`${item.code}?name=${name}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };

  const validPatientId =
    paramPatientId ||
    visitResponse?.data?.[0]?.patient ||
    contractData?.patient;
  const handleNew = () => {
    if (validPatientId) {
      navigate(`new?patient=${validPatientId}`);
    }
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        {/* <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        /> */}
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterVisit />
      </Sidebar>
    </section>
  );
  const dateTimeColumn = (cell: GetVisitModel) => {
    if (!cell.date || !cell.time) return "";
    const dateFm = format(cell.date, "yyyy-MM-dd");
    const properDate = `${dateFm} ${cell.time}`;
    const dateConfigFm = formatDateLocal(cell.date);
    const time = formatTimeLocal(properDate);
    return (
      <span>
        {dateConfigFm} {time}
      </span>
    );
  };
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{visitResponse?.count}</b>
      </h5>

      <DataTable
        value={visitResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column field="session" header="Session" sortable></Column>
        <Column
          body={(cell) => <b>{cell.service_name}</b>}
          field="service_name"
          header="Contract"
          sortable
        ></Column>
        <Column
          body={dateTimeColumn}
          field="date"
          header="Date"
          sortable
        ></Column>
        <Column field="description" header="Description" sortable></Column>
        <Column
          field="status"
          header="Status"
          body={(cell) => (
            <Tag
              value={cell?.status}
              severity={getPaymentSeverity(cell?.status || "")}
            />
          )}
        ></Column>
        <Column
          body={(cell) => (
            <b>
              {cell.person_first_name} {cell.person_last_name}
            </b>
          )}
          field="person"
          header="Doctor"
          sortable
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={visitResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListVisit" className="visit">
      <HeaderContent title={<h3>Visit</h3>} className="bg-slate-200">
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListVisit;
